import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import {
  assignTdsWidths,
  getTdWidths,
  copyTableProportions,
} from 'common/utils/dom-util';

import FastScrollbox from 'common/components/fast-scrollbox';

import Column from '../column';
import Row from '../row';
import RowGroup from '../row-group';

import Sticky from 'common/components/sticky';
import {t} from 'common/utils/translation-util';

export default class Table extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    className: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.array),
    emptySearch: PropTypes.element,
    headers: PropTypes.array,
    height: PropTypes.number,
    rowGroups: PropTypes.bool,
    rowHeaders: PropTypes.bool,
    rowHeadersColumn: PropTypes.any,
    scroll: PropTypes.bool,
    sticky: PropTypes.bool,
    stickyContainer: PropTypes.string,
    style: PropTypes.object,
    summaryData: PropTypes.array,
    type: PropTypes.oneOf(['striped', 'form-grid']),
    zeroState: PropTypes.element,
  };

  static defaultProps = {
    data: null,
    emptySearch: (
      <p>
        <em>{t('Nothing')}</em>
      </p>
    ),
    headers: null,
    height: null,
    rowGroups: false,
    rowHeaders: false,
    rowHeadersColumn: null,
    scroll: false,
    sticky: false,
    style: {},
    type: 'striped',
    zeroState: (
      <p>
        <em>{t('Empty')}</em>
      </p>
    ),
  };

  constructor(props) {
    super(props);

    this.enableSticky = props.sticky && !window.karmaIsRunning;

    if (this.enableSticky) {
      this.adjustProportions = this.adjustProportions.bind(this);
    }
  }

  componentDidMount() {
    if (this.enableSticky) {
      this.adjustProportions();
      window.addEventListener('resize', this.adjustProportions);
    }
  }

  componentWillUnmount() {
    if (this.enableSticky) {
      window.removeEventListener('resize', this.adjustProportions);
    }
  }

  // When the header is sticky, it is positioned absolutely, which gets
  // rid of the natural widths of the cells.  This function manually grabs
  // the widths that would have been if not positioned absolute and assigns
  // the widths to the header row and the sticky row cells
  adjustProportions() {
    copyTableProportions(this._firstRow, this._stickyHeaderRow);
    const newHeaderWidths = getTdWidths(this._stickyHeaderRow);
    assignTdsWidths(this._firstRow, newHeaderWidths);
  }

  /* Sorts out existing child rows and columns */
  filterChildren(children) {
    const rows = [];

    // Flatten list of Row and Row Groups into just a list of generated Rows
    for (const child of children) {
      switch (child.type) {
        case Row:
          rows.push(child);
          break;
        case RowGroup:
          for (const row of RowGroup.toRows(child.props)) {
            rows.push(row);
          }
          break;
        default:
          break;
      }
    }

    return {
      columns: children.filter((component) => component.type === Column),
      rowChildren: rows,
    };
  }

  render() {
    const children = React.Children.toArray(this.props.children);
    const {columns, rowChildren} = this.filterChildren(children);

    // Extract row props for filtering, etc
    const rowProps = rowChildren.map((row) => row.props);

    // Auto-generate rows from data property
    if (this.props.data) {
      for (const datum of this.props.data) {
        rowProps.push({data: datum});
      }
    }

    // Auto-generate column headers from headers property
    if (this.props.headers) {
      columns.push(
        ...this.props.headers.map((datum, index) => (
          <Column key={index}>{datum}</Column>
        )),
      );
    }

    // Add a dummy column, if we are using row headers
    if (this.props.rowHeaders) {
      columns.unshift(
        <Column key={'row-dummy'}>{this.props.rowHeadersColumn}</Column>,
      );
    }

    // Return zero state if no rows
    if (rowProps.length === 0) {
      return this.props.zeroState;
    }

    // Filters rows based on column filter property
    const displayedRowProps = Column.applyFilters(columns, rowProps);
    if (displayedRowProps.length === 0) {
      return this.props.emptySearch;
    }

    // Apply sorting (in place)
    Column.applySorting(columns, displayedRowProps);
    let tBody = null;
    const props = {};

    if (this.props.scroll) {
      if (this.enableSticky) {
        throw new Error(
          'cannot use sticky with scroll (implementation pending)',
        );
      }

      // If scroll is specified
      const columnCount = columns.length;
      const dummyCells = Array.from(Array(columnCount).keys(), (index) => (
        <td key={index} />
      ));
      const childHeight = 50; // TODO need to determine this from CSS

      // Fetches a single row
      const buildRow = (index) => (
        <Row {...displayedRowProps[index]} key={index} />
      );

      // Creates the scrollbox
      tBody = (
        <FastScrollbox
          buildChild={buildRow}
          childCount={displayedRowProps.length}
          childHeight={childHeight}
          pageScroll={true}
          rootTag={'tbody'}
          spacerContents={dummyCells}
          spacerTag={'tr'}
          trim={true}
        />
      );
    } else {
      // Simply use rows from props
      const buildRow = (myRowProps, index) => (
        <Row {...myRowProps} key={index} />
      );

      const firstRowInitialProps = displayedRowProps.shift();

      // FIXME use of deprecated findDOMNode to assign ref outside of render. Wowza.
      const firstRowProps = {
        ...firstRowInitialProps,
        ref: (row) => (this._firstRow = ReactDOM.findDOMNode(row)),
      };

      displayedRowProps.unshift(firstRowProps);

      const displayedRows = displayedRowProps.map(buildRow);

      tBody = <tbody>{displayedRows}</tbody>;
    }

    // Set up table style classes
    const classes = ['Table', `Table--${this.props.type}`];

    if (this.props.className) {
      classes.push(this.props.className);
    }

    if (this.enableSticky) {
      // Adds sticky CSS properties
      classes.push('Table--sticky');
    }

    if (this.props.rowGroups) {
      classes.push('Table--row-groups');
    }

    const tHead = !this.enableSticky ? (
      <thead>
        <tr>{columns}</tr>
      </thead>
    ) : (
      <Sticky containerSelector={this.props.stickyContainer} tagName={'thead'}>
        <tr ref={(header) => (this._stickyHeaderRow = header)}>{columns}</tr>
      </Sticky>
    );

    const summaryData = !this.props.summaryData ? null : (
      <Row data={this.props.summaryData} isSummaryRow={true} />
    );

    return (
      <table className={classes.join(' ')} {...props} style={this.props.style}>
        {tHead}
        {tBody}
        {summaryData}
      </table>
    );
  }
}
